import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';

import './gp.css';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import CustomerMaster from './pages/CustomerMaster';
import ProductMaster from './pages/ProductMaster';
import SalesmanMaster from './pages/SalesmanMaster';
import SalesUpload from './pages/SalesUpload';
import SalesDataManagement from './pages/SalesDataManagement';
import MonthWiseAnalysis from './pages/MonthWiseAnalysis';
import BriefReport from './pages/BriefReport';
import PriceChecker from './pages/PriceChecker';
import ApiKeys from './pages/ApiKeys';
import GpTargets from './pages/GpTargets';
import GpAnalysis from './pages/GpAnalysis';
import Maintenance from './pages/Maintenance';
import { useAuth } from './context/AuthContext';
import { getSettings } from './api/settings';

/**
 * The GP report module's entry point — the standalone app's `App.jsx`, minus the
 * three things reception already provides: the router, the auth provider, and the
 * login route.
 *
 * `.gp-root` is the class every Tailwind rule in this module is scoped to
 * (`gp.css`), so it must stay on the outermost element — remove it and every page
 * below loses its styling at once, which is the intended failure mode: obvious
 * rather than subtle.
 *
 * Maintenance mode is checked here rather than by a redirect. The standalone app
 * bounced to its own `/maintenance` URL; inside reception the module simply renders
 * that screen in place, so a locked GP module never takes over the whole app.
 */
const GpReport = () => {
    const { user } = useAuth();
    const isSystemAdmin = user?.role === 'system_admin';
    const [maintenance, setMaintenance] = useState(null); // null = still checking

    useEffect(() => {
        if (isSystemAdmin) {
            setMaintenance(false);
            return;
        }
        // Admins are never gated server-side, so this only matters for everyone
        // else — it catches maintenance mode being already on when the page loads,
        // not just an in-flight request coming back 503.
        getSettings()
            .then((s) => setMaintenance(!!s.maintenanceMode))
            .catch(() => setMaintenance(false));
    }, [isSystemAdmin]);

    if (maintenance === null) return null;

    return (
        <div className="gp-root">
            <ErrorBoundary>
                {maintenance ? (
                    <Maintenance />
                ) : (
                    <Routes>
                        <Route element={<Layout />}>
                            <Route index element={<Dashboard />} />
                            <Route path="customers" element={<CustomerMaster />} />
                            <Route path="products" element={<ProductMaster />} />
                            <Route path="salesmen" element={<SalesmanMaster />} />
                            <Route path="sales-upload" element={<SalesUpload />} />
                            <Route path="sales-data" element={<SalesDataManagement />} />
                            <Route path="month-analysis" element={<MonthWiseAnalysis />} />
                            <Route path="brief-report" element={<BriefReport />} />
                            <Route path="price-checker" element={<PriceChecker />} />
                            <Route path="gp-analysis" element={<GpAnalysis />} />
                            <Route path="gp-targets" element={<GpTargets />} />
                            <Route path="api-keys" element={<ApiKeys />} />
                        </Route>
                    </Routes>
                )}
            </ErrorBoundary>
        </div>
    );
};

export default GpReport;
